import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { calibrationApi, CalibrationMetrics, VersionComparisonItem } from '../utils/api'
import { getBrierClass, getBrierLabel, formatTimeAgo } from '../utils/format'
import clsx from 'clsx'

// Zone order for consistent display
const ZONE_ORDER = [
  'extreme_low',
  'tail_low',
  'moderate_low',
  'mid',
  'moderate_high',
  'tail_high',
  'extreme_high',
]

const ZONE_LABELS: Record<string, string> = {
  extreme_low: '<10%',
  tail_low: '10-20%',
  moderate_low: '20-35%',
  mid: '35-65%',
  moderate_high: '65-80%',
  tail_high: '80-90%',
  extreme_high: '>90%',
}

function RollingWindowSelector({
  value,
  onChange,
}: {
  value: number
  onChange: (days: number) => void
}) {
  const options = [7, 14, 30, 60, 90, 180]
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-star-muted font-mono">Window:</span>
      <div className="flex gap-1">
        {options.map((days) => (
          <button
            key={days}
            onClick={() => onChange(days)}
            className={clsx(
              'px-2 py-1 rounded text-xs font-mono transition-colors',
              value === days
                ? 'bg-signal-blue/20 text-signal-blue border border-signal-blue/40'
                : 'bg-void-100 text-star-muted border border-star-dim/30 hover:bg-void-200'
            )}
          >
            {days}d
          </button>
        ))}
      </div>
    </div>
  )
}

function MetricCard({
  label,
  value,
  subvalue,
  colorClass,
  tooltip,
}: {
  label: string
  value: string
  subvalue?: string
  colorClass?: string
  tooltip?: string
}) {
  return (
    <div className="glass-card p-4" title={tooltip}>
      <div className="data-label text-[10px] sm:text-xs">{label}</div>
      <div className={clsx('text-xl sm:text-2xl font-mono font-semibold mt-1', colorClass)}>
        {value}
      </div>
      {subvalue && <div className="text-xs text-star-muted mt-1">{subvalue}</div>}
    </div>
  )
}

function ZoneAccuracyChart({ metrics }: { metrics: CalibrationMetrics }) {
  const data = ZONE_ORDER.filter((zone) => metrics.accuracy_by_zone[zone])
    .map((zone) => ({
      zone,
      label: ZONE_LABELS[zone],
      accuracy: metrics.accuracy_by_zone[zone].accuracy * 100,
      total: metrics.accuracy_by_zone[zone].total,
    }))

  if (data.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-star-dim text-xs">
        No zone data available
      </div>
    )
  }

  return (
    <div className="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#555566', fontSize: 10, fontFamily: 'monospace' }}
          />
          <YAxis
            domain={[0, 100]}
            tickFormatter={(v) => `${v}%`}
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#555566', fontSize: 10, fontFamily: 'monospace' }}
          />
          <Tooltip
            content={({ payload }) => {
              if (!payload?.[0]) return null
              const d = payload[0].payload
              return (
                <div className="glass-card p-3 text-xs font-mono shadow-lg">
                  <div className="text-star font-semibold mb-1">{d.label}</div>
                  <div className="space-y-1">
                    <div className="flex justify-between gap-4">
                      <span className="text-star-dim">Accuracy</span>
                      <span className="text-signal-blue">{d.accuracy.toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-star-dim">Samples</span>
                      <span className="text-star">{d.total}</span>
                    </div>
                  </div>
                </div>
              )
            }}
          />
          <Bar dataKey="accuracy" radius={[4, 4, 0, 0]}>
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.accuracy >= 50 ? '#00ff88' : '#ff6b6b'}
                fillOpacity={0.7}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

function VersionComparisonTable({
  versions,
  currentVersion,
}: {
  versions: VersionComparisonItem[]
  currentVersion: string
}) {
  if (versions.length === 0) {
    return (
      <div className="p-8 text-center text-star-dim text-xs font-mono">
        No versions with enough data to compare
      </div>
    )
  }

  // Sort by version descending (newest first)
  const sortedVersions = [...versions].sort((a, b) => b.version.localeCompare(a.version))

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-star-dim/20">
            <th className="text-left p-3 font-mono text-star-muted font-normal">Version</th>
            <th className="text-right p-3 font-mono text-star-muted font-normal">Predictions</th>
            <th className="text-right p-3 font-mono text-star-muted font-normal">Avg Brier</th>
            <th className="text-right p-3 font-mono text-star-muted font-normal">Weighted</th>
            <th className="text-right p-3 font-mono text-star-muted font-normal">Log Loss</th>
            <th className="text-right p-3 font-mono text-star-muted font-normal">Date Range</th>
          </tr>
        </thead>
        <tbody>
          {sortedVersions.map((v) => (
            <tr
              key={v.version}
              className={clsx(
                'border-b border-star-dim/10 hover:bg-void-50/50',
                v.version === currentVersion && 'bg-signal-blue/5'
              )}
            >
              <td className="p-3">
                <span className="font-mono text-star">{v.version}</span>
                {v.version === currentVersion && (
                  <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-signal-blue/20 text-signal-blue">
                    current
                  </span>
                )}
              </td>
              <td className="p-3 text-right font-mono text-star">{v.resolved_predictions}</td>
              <td className={clsx('p-3 text-right font-mono', getBrierClass(v.avg_brier))}>
                {v.avg_brier !== null ? v.avg_brier.toFixed(4) : '—'}
              </td>
              <td className={clsx('p-3 text-right font-mono', getBrierClass(v.weighted_brier))}>
                {v.weighted_brier !== null ? v.weighted_brier.toFixed(4) : '—'}
              </td>
              <td className="p-3 text-right font-mono text-star-muted">
                {v.avg_log_loss !== null ? v.avg_log_loss.toFixed(4) : '—'}
              </td>
              <td className="p-3 text-right font-mono text-star-dim text-[10px]">
                {v.oldest_prediction && v.newest_prediction
                  ? `${formatTimeAgo(v.oldest_prediction)} - ${formatTimeAgo(v.newest_prediction)}`
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function Calibration() {
  const [rollingDays, setRollingDays] = useState(30)

  const { data: metrics, isLoading: metricsLoading, refetch: refetchMetrics } = useQuery({
    queryKey: ['calibration', 'metrics', rollingDays],
    queryFn: () => calibrationApi.metrics({ rolling_days: rollingDays }),
    refetchInterval: 120000,
  })

  const { data: versions, isLoading: versionsLoading } = useQuery({
    queryKey: ['calibration', 'by-version', rollingDays],
    queryFn: () => calibrationApi.byVersion(rollingDays),
    refetchInterval: 120000,
  })

  const { data: settings } = useQuery({
    queryKey: ['calibration', 'settings'],
    queryFn: calibrationApi.currentVersion,
  })

  const isLoading = metricsLoading || versionsLoading

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-display font-semibold text-star mb-1 sm:mb-2">
            Calibration
          </h2>
          <p className="text-star-muted text-xs sm:text-sm">
            Prediction accuracy metrics and version tracking
          </p>
        </div>
        <div className="flex items-center gap-3">
          <RollingWindowSelector value={rollingDays} onChange={setRollingDays} />
          <button
            className="btn-secondary min-h-[44px] text-xs sm:text-sm"
            onClick={() => refetchMetrics()}
          >
            <span aria-hidden="true">↻</span> Refresh
          </button>
        </div>
      </div>

      {/* Current version badge */}
      {settings && (
        <div className="mb-4 flex items-center gap-3 text-xs font-mono">
          <span className="px-2 py-1 rounded bg-signal-blue/10 text-signal-blue border border-signal-blue/30">
            Algorithm: {settings.algorithm_version}
          </span>
          <span className="text-star-dim">
            Decay: λ={settings.time_decay_lambda} | Min samples: {settings.min_samples_for_metrics}
          </span>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-full border-2 border-signal-blue/30 border-t-signal-blue animate-spin" />
            <p className="text-star-muted font-mono text-sm">Loading calibration data...</p>
          </div>
        </div>
      )}

      {/* Metrics */}
      {metrics && (
        <>
          {/* Overview stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6"
          >
            <MetricCard
              label="Resolved Predictions"
              value={metrics.resolved_predictions.toString()}
              subvalue={`of ${metrics.total_predictions} total`}
            />
            <MetricCard
              label="Avg Brier Score"
              value={metrics.avg_brier !== null ? metrics.avg_brier.toFixed(4) : '—'}
              subvalue={getBrierLabel(metrics.avg_brier)}
              colorClass={getBrierClass(metrics.avg_brier)}
              tooltip="Lower is better. <0.10 = Excellent, <0.15 = Good, <0.25 = Fair"
            />
            <MetricCard
              label="Time-Weighted Brier"
              value={metrics.weighted_brier !== null ? metrics.weighted_brier.toFixed(4) : '—'}
              subvalue="Recent weighted more"
              colorClass={getBrierClass(metrics.weighted_brier)}
              tooltip="Brier score with exponential decay on older predictions"
            />
            <MetricCard
              label="Avg Log Loss"
              value={metrics.avg_log_loss !== null ? metrics.avg_log_loss.toFixed(4) : '—'}
              subvalue="Lower is better"
            />
            <MetricCard
              label="Bullish Accuracy"
              value={
                metrics.bullish_accuracy !== null
                  ? `${(metrics.bullish_accuracy * 100).toFixed(1)}%`
                  : '—'
              }
              subvalue="When we predict > market"
              colorClass={
                metrics.bullish_accuracy !== null && metrics.bullish_accuracy >= 0.5
                  ? 'text-signal-green'
                  : 'text-signal-red'
              }
            />
            <MetricCard
              label="Bearish Accuracy"
              value={
                metrics.bearish_accuracy !== null
                  ? `${(metrics.bearish_accuracy * 100).toFixed(1)}%`
                  : '—'
              }
              subvalue="When we predict < market"
              colorClass={
                metrics.bearish_accuracy !== null && metrics.bearish_accuracy >= 0.5
                  ? 'text-signal-green'
                  : 'text-signal-red'
              }
            />
          </motion.div>

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Zone accuracy chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-card p-4"
            >
              <h3 className="font-mono text-sm text-star-muted mb-4">Accuracy by Probability Zone</h3>
              <ZoneAccuracyChart metrics={metrics} />
              <p className="text-[10px] text-star-dim mt-2">
                Shows prediction accuracy for each probability range. Green = above 50%, Red = below
                50%.
              </p>
            </motion.div>

            {/* Directional edge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="glass-card p-4"
            >
              <h3 className="font-mono text-sm text-star-muted mb-4">Directional Performance</h3>
              <div className="grid grid-cols-2 gap-4 h-48">
                <div className="flex flex-col items-center justify-center bg-void-50 rounded-lg p-4">
                  <span className="text-3xl mb-2">📈</span>
                  <span className="text-xs text-star-dim mb-1">Bullish Calls</span>
                  <span
                    className={clsx(
                      'text-2xl font-mono font-semibold',
                      metrics.bullish_accuracy !== null && metrics.bullish_accuracy >= 0.5
                        ? 'text-signal-green'
                        : 'text-signal-red'
                    )}
                  >
                    {metrics.bullish_accuracy !== null
                      ? `${(metrics.bullish_accuracy * 100).toFixed(1)}%`
                      : '—'}
                  </span>
                  <span className="text-[10px] text-star-dim mt-1">when our prob &gt; market</span>
                </div>
                <div className="flex flex-col items-center justify-center bg-void-50 rounded-lg p-4">
                  <span className="text-3xl mb-2">📉</span>
                  <span className="text-xs text-star-dim mb-1">Bearish Calls</span>
                  <span
                    className={clsx(
                      'text-2xl font-mono font-semibold',
                      metrics.bearish_accuracy !== null && metrics.bearish_accuracy >= 0.5
                        ? 'text-signal-green'
                        : 'text-signal-red'
                    )}
                  >
                    {metrics.bearish_accuracy !== null
                      ? `${(metrics.bearish_accuracy * 100).toFixed(1)}%`
                      : '—'}
                  </span>
                  <span className="text-[10px] text-star-dim mt-1">when our prob &lt; market</span>
                </div>
              </div>
              {metrics.avg_edge !== null && (
                <div className="mt-4 pt-4 border-t border-star-dim/20 text-center">
                  <span className="text-xs text-star-dim">Average Edge: </span>
                  <span
                    className={clsx(
                      'font-mono font-semibold',
                      metrics.avg_edge >= 0 ? 'text-signal-green' : 'text-signal-red'
                    )}
                  >
                    {metrics.avg_edge >= 0 ? '+' : ''}
                    {(metrics.avg_edge * 100).toFixed(2)}%
                  </span>
                </div>
              )}
            </motion.div>
          </div>

          {/* Date range */}
          {(metrics.oldest_prediction || metrics.newest_prediction) && (
            <div className="mb-6 text-xs text-star-dim font-mono text-center">
              Data range: {metrics.oldest_prediction ? formatTimeAgo(metrics.oldest_prediction) : '?'}{' '}
              → {metrics.newest_prediction ? formatTimeAgo(metrics.newest_prediction) : 'now'}
            </div>
          )}
        </>
      )}

      {/* Version comparison */}
      {versions && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-star-dim/20">
            <h3 className="font-display font-semibold text-star flex items-center gap-2">
              <span className="text-signal-purple" aria-hidden="true">
                ◈
              </span>
              Version Comparison
            </h3>
            <p className="text-xs text-star-muted mt-1">
              Compare calibration across algorithm versions (last {rollingDays} days)
            </p>
          </div>
          <VersionComparisonTable
            versions={versions.versions}
            currentVersion={versions.current_version}
          />
        </motion.div>
      )}

      {/* Empty state */}
      {!isLoading && metrics && metrics.resolved_predictions === 0 && (
        <div className="glass-card p-12 text-center">
          <p className="text-star-muted font-mono text-lg mb-2">No resolved predictions yet</p>
          <p className="text-star-dim text-sm">
            Calibration data will appear here once predictions are resolved
          </p>
        </div>
      )}
    </div>
  )
}
